import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  getFriendStatusBySessionToken,
  getFriendStatusByLineUserId,
  getLinkLineBotAccount,
  saveToken,
  prepareSignedLineOAuthState,
} from 'api';
import {
  detectPWA,
  isInvalidLineSessionError,
  isLineLoginEnabled,
} from 'utils';
import { routes, storageKeys } from '../constants';
import useToast from './useToast';
import type {
  TAffiliateInfo,
  TGetFriendStatusResponse,
  TLineOAuthTokenResponse,
} from 'types';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import { buildLocalLineOAuthCallbackUrl } from 'utils/localLineOAuth';

const lineApi = process.env.REACT_APP_LINE_API_LOGIN || '';
const lineChannelId = process.env.REACT_APP_LINE_CHANNEL_ID || '';
const affiliatePid = process.env.REACT_APP_AFFILIATE_PID || '';

const AUTH_LOGIN_LINE_ENDPOINT =
  'authorize?response_type=code&client_id=$channelId&redirect_uri=$uri&state=$state&bot_prompt=normal&scope=profile%20openid&nonce=$nonce';

type TLineGate = 'NONE' | 'LINE' | 'LINE_FRIEND' | 'ALL_CLEAR';

const getAffiliateInfoFromStorage = (): TAffiliateInfo | undefined => {
  const affiliateInfo: TAffiliateInfo = {
    affiliate_pid: affiliatePid || null,
    affiliate_cid: affiliatePid
      ? localStorage.getItem(`CL_${affiliatePid}`)
      : null,
    affiliate_mid: localStorage.getItem('mid'),
    affiliater_id: localStorage.getItem('affliater_id'),
  };

  return Object.values(affiliateInfo).some((value) => !!value)
    ? affiliateInfo
    : undefined;
};

function useLine() {
  const { toastError } = useToast();
  const navigate = useNavigate();
  const { user, setIsAuthenticated, setUserRefreshRequested } = useUserStore();

  const [basicId, setBasicId] = useState<string>(() => {
    return localStorage.getItem(storageKeys.BASIC_ID) || '';
  });

  const updateBasicId = useCallback(
    (v: string) => {
      if (!v || basicId === v) return;
      localStorage.setItem(storageKeys.BASIC_ID, v);
      setBasicId(v);
    },
    [basicId, setBasicId],
  );

  const [lineSessionExpiresAtMs, setLineSessionExpiresAtMs] = useState<
    number | null
  >(() => {
    const raw = localStorage.getItem(storageKeys.LINE_SESSION_EXPIRES_AT_MS);
    return raw ? Number(raw) : null;
  });

  const updateLineSessionExpiresAtMs = useCallback((v: number | null) => {
    if (v == null || Number.isNaN(v)) {
      localStorage.removeItem(storageKeys.LINE_SESSION_EXPIRES_AT_MS);
      setLineSessionExpiresAtMs(null);
      return;
    }
    localStorage.setItem(storageKeys.LINE_SESSION_EXPIRES_AT_MS, String(v));
    setLineSessionExpiresAtMs(v);
  }, []);

  const isLineSessionExpired = useMemo(() => {
    if (!lineSessionExpiresAtMs) return true;
    return new Date(lineSessionExpiresAtMs).getTime() < Date.now();
  }, [lineSessionExpiresAtMs]);

  const [lineGate, setLineGate] = useState<TLineGate>('NONE');

  const [lineSessionToken, setLineSessionToken] = useState<string | undefined>(
    () => {
      return localStorage.getItem(storageKeys.LINE_SESSION_TOKEN) ?? undefined;
    },
  );

  const updateLineSessionToken = useCallback((token?: string) => {
    if (token) {
      localStorage.setItem(storageKeys.LINE_SESSION_TOKEN, token);
      setLineSessionToken(token);
    } else {
      localStorage.removeItem(storageKeys.LINE_SESSION_TOKEN);
      setLineSessionToken(undefined);
    }
  }, []);

  const activeLineSessionToken =
    isLineLoginEnabled && !isLineSessionExpired ? lineSessionToken : undefined;

  // ====== helper functions ======
  const clearLineGate = useCallback(() => {
    setLineGate('NONE');
  }, []);

  const applyLineSessionResponse = useCallback(
    async (data: TLineOAuthTokenResponse) => {
      const { appAuth, lineBotBasicId, lineSession } = data;

      updateBasicId(lineBotBasicId);
      updateLineSessionToken(lineSession?.token ?? undefined);

      const expiresAtMs = Date.parse(lineSession?.expiresAt ?? '');
      updateLineSessionExpiresAtMs(
        Number.isFinite(expiresAtMs) ? expiresAtMs : null,
      );

      clearLineGate();
      setUserRefreshRequested(true);
      const accessToken = appAuth?.accessToken ?? null;
      const refreshToken = appAuth?.refreshToken ?? null;

      if (accessToken && refreshToken) {
        saveToken({ accessToken, refreshToken });
        setIsAuthenticated(true);

        navigate(routes.MY_PAGE, { replace: true });
        return;
      }
      navigate(routes.MY_PAGE, { replace: true });
    },
    [
      clearLineGate,
      setUserRefreshRequested,
      updateBasicId,
      updateLineSessionToken,
      updateLineSessionExpiresAtMs,
      setIsAuthenticated,
      navigate,
    ],
  );

  // ====== API mutations ======
  const {
    mutateAsync: mutateGetLinkLineBotAccount,
    isPending: isGetLinkPending,
  } = useMutation({
    mutationFn: (basicIdParam: string) => getLinkLineBotAccount(basicIdParam),
    onError(error) {
      toastError(error);
    },
  });

  const {
    mutateAsync: mutateGetFriendStatus,
    isPending: isGetFriendStatusPending,
  } = useMutation({
    mutationFn: async () => {
      if (!isLineLoginEnabled) return true;

      let res: TGetFriendStatusResponse = { status: false, basicId: '' };
      if (user?.lineUserId) {
        res = await getFriendStatusByLineUserId({
          lineUserId: user.lineUserId,
          basicId,
        });
      } else if (activeLineSessionToken) {
        res = await getFriendStatusBySessionToken({
          lineSessionToken: activeLineSessionToken,
          basicId,
        });
      }
      updateBasicId(res.basicId);
      return res.status;
    },
    onError(error) {
      if (isInvalidLineSessionError(error)) {
        clearLineSession();
        setLineGate('LINE');
        return;
      }
      toastError(error as Error);
    },
  });

  // ====== Actions ======
  const clearLineSession = useCallback(() => {
    updateLineSessionToken(undefined);
    localStorage.removeItem(storageKeys.LINE_SESSION_EXPIRES_AT_MS);
    updateLineSessionExpiresAtMs(null);
  }, [updateLineSessionToken, updateLineSessionExpiresAtMs]);

  const redirectLogin = useCallback(async () => {
    if (!isLineLoginEnabled) return;

    const fromPath = (() => {
      const url = new URL(window.location.href);
      return `${url.pathname}${url.search}${url.hash}`;
    })();

    const { state, nonce } = await prepareSignedLineOAuthState({
      from: fromPath,
      maleId: user?.id,
      affiliateInfo: getAffiliateInfoFromStorage(),
    });

    const callbackUrl = `${window.location.origin}${routes.LINE_CALLBACK}`;

    const localCallbackUrl = buildLocalLineOAuthCallbackUrl({
      callbackUrl,
      state,
      storage: localStorage,
      nodeEnv: process.env.NODE_ENV,
      localMode: process.env.REACT_APP_TECO_LOCAL_MODE,
    });
    if (localCallbackUrl) {
      window.location.assign(localCallbackUrl);
      return;
    }

    const authorizeUrl = `${lineApi}${AUTH_LOGIN_LINE_ENDPOINT}`
      .replace('$channelId', lineChannelId)
      .replace('$uri', encodeURIComponent(callbackUrl))
      .replace('$state', encodeURIComponent(state))
      .replace('$nonce', nonce);

    const isPWA = detectPWA();
    window.location.assign(
      isPWA ? `${authorizeUrl}&disable_auto_login=true` : authorizeUrl,
    );
  }, [user?.id]);

  const loginOrRedirectLineOAuth = useCallback(async () => {
    await redirectLogin();
  }, [redirectLogin]);

  const goToAddLineBotAccount = useCallback(
    async (onBeforeNavigate?: () => void) => {
      const res = await mutateGetLinkLineBotAccount(basicId || '');
      if (!res?.basicId) return;

      updateBasicId(res.basicId);
      if (onBeforeNavigate) onBeforeNavigate();
      if (
        process.env.NODE_ENV !== 'production' &&
        process.env.REACT_APP_TECO_LOCAL_MODE === 'true'
      )
        return;
      window.location.href = `${process.env.REACT_APP_LINE_BOT_URL}${res.basicId}`;
    },
    [basicId, mutateGetLinkLineBotAccount, updateBasicId],
  );

  const ensureLineGate = useCallback(async (): Promise<boolean> => {
    if (!isLineLoginEnabled) {
      clearLineGate();
      return true;
    }

    if (!user?.lineUserId) {
      setLineGate('LINE');
      if (user && activeLineSessionToken) {
        clearLineSession();
      }
      return false;
    }

    try {
      const isFriend = await mutateGetFriendStatus();
      if (!isFriend) {
        setLineGate('LINE_FRIEND');
        return false;
      }
      setLineGate('ALL_CLEAR');
      return true;
    } catch {
      setLineGate('LINE_FRIEND');
      return false;
    }
  }, [
    user,
    activeLineSessionToken,
    mutateGetFriendStatus,
    clearLineSession,
    clearLineGate,
  ]);

  return {
    // state
    lineSessionToken,
    basicId,
    lineSessionExpiresAtMs,
    isLineSessionExpired,
    lineGate,
    activeLineSessionToken,
    // setters
    updateBasicId,
    updateLineSessionExpiresAtMs,

    // actions
    redirectLogin,
    loginOrRedirectLineOAuth,
    goToAddLineBotAccount,
    applyLineSessionResponse,
    clearLineSession,
    clearLineGate,
    ensureLineGate,

    // mutations
    mutateGetFriendStatus,
    isGetFriendStatusPending,
    isGetLinkPending,
  };
}

export default useLine;
